
import { LineItemStatusSummary } from "@/models/OrderLineItemSummary";
import ExpandTransition from "@/components/ExpandTransition";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import VoidLabelConfirmationDialog from "@/components/Shipping/VoidLabelConfirmationDialog.vue";
import gql from "graphql-tag";
import {
	HardwareIntegrationService,
	getHardwareService,
} from "@/services/HardwareIntegrationService";
import { Printer } from "@/types/hardware";
import { shouldEnableHardware } from "@/models/User";
import { printStoredLabel, labelPdfUrl } from "@/services/LabelPdfService";
import {
	getPrinterConfiguration,
	PrinterConfiguration,
} from "@/services/PrinterConfigurationService";

interface customLineItem extends LineItemStatusSummary {
	decommit: boolean;
	removeTracking: boolean;
	show: boolean;
	lineItemId: number;
	pickTicketId: string | null;
	normalPickTicketId: string | null;
}
interface PickTicket {
	pickTicketNumber: string | null;
	trackingNumber: string | null;
	pickTicketId: number;
	normalPickTicketId: number | null;
	shippingDate: string | null;
	formattedShippingDate: string | null;
	printDate: string | null;
	formattedPrintDate: string | null;
	items: customLineItem[];
	isShipped: boolean;
	shippingStatus: string;
	show: boolean;
	decommit: boolean;
	removeTracking: boolean;
	type: string;
	finalShipStationShipmentId?: string;
	finalShipStationSalesOrderId?: string;
}

// GraphQL query to fetch shipping label by pickTicketId
const GET_SHIPPING_LABEL_BY_PICK_TICKET = gql`
	query GetShippingLabelByPickTicket($pickTicketId: Long!) {
		shippingLabels(where: {
			pickTicketId: { eq: $pickTicketId }
			isReturnLabel: { eq: false }
		}, order: { packageSequence: ASC }) {
			shippingLabelId
			labelId
			shipmentId
			trackingNumber
			trackingUrl
			carrierCode
			carrierFriendlyName
			serviceCode
			shippingCost
			labelData
			labelFormat
			isVoided
			voidedAt
			isReturnLabel
			isTestLabel
			trackingStatus
			estimatedDeliveryDate
			createdAt
			updatedAt
			packageSequence
			totalPackages
			combinedLabelData
		}
	}
`;

// GraphQL query to fetch return label by pickTicketId
const GET_RETURN_LABEL_BY_PICK_TICKET = gql`
	query GetReturnLabelByPickTicket($pickTicketId: Long!) {
		returnLabels: shippingLabels(
			where: {
				pickTicketId: { eq: $pickTicketId }
				isReturnLabel: { eq: true }
			}
		) {
			shippingLabelId
			labelId
			trackingNumber
			trackingUrl
			labelData
			carrierCode
			carrierFriendlyName
			shippingCost
			isVoided
			voidedAt
			createdAt
			originalLabelId
			packageSequence
		}
	}
`;

// GraphQL mutation to void a shipping label
const VOID_SHIPPING_LABEL = gql`
	mutation VoidShippingLabel($labelId: String!) {
		voidShippingLabel(labelId: $labelId) {
			success
			message
			returnLabelVoided
		}
	}
`;

// GraphQL mutation to create return label
const CREATE_RETURN_LABEL = gql`
	mutation CreateReturnLabel($labelId: String!, $returnLabelMessages: LabelMessagesInput) {
		createReturnLabel(labelId: $labelId, returnLabelMessages: $returnLabelMessages) {
			label {
				shippingLabelId
				labelId
				trackingNumber
				labelData
				shippingCost
				isReturnLabel
			}
		}
	}
`;

export default {
	name: "PickTicketStatus",
	components: {
		ExpandTransition,
		SystemCategoryIcon,
		VoidLabelConfirmationDialog,
	},
	props: {
		pickTickets: {
			type: Array as () => PickTicket[],
			required: true,
		},
		statusTypes: {},
		orderId: {
			type: Number,
		},
		isCancelled: {
			type: Boolean,
			default: false,
		},
		canPrint: {
			type: Boolean,
			default: false,
		},
		trackingUrls: {
			type: Object,
			default: () => ({}),
		},
		lineItems: {
			type: Array,
			default: () => [],
		},
	},
	data(): {
		notShipped: boolean;
		notShipped2: boolean;
		showDateByRow: number | null;
		showPrintedDateByRow: number | null;
		showItemTableByRow: string | null;
		showSubTableByRow: string | null;
		ticketsPick: PickTicket[];
		showTable: string[];
		ptUpdated: boolean;
		tempTrackingNumbers: { [key: number]: string };
		editTrackingMode: { [key: number]: boolean };
		labelUrls: { [key: number]: string };
		labelPdfRefs: { [pickTicketId: number]: { shippingLabelId: number; combined: boolean } };
		hardwareService: HardwareIntegrationService | null;
		hardwareAvailable: boolean;
		availablePrinters: Printer[];
		selectedPrinterId: string | null;
		printingLabels: { [key: number]: boolean };
		printingReturnLabels: { [key: number]: boolean };
		printerConnecting: boolean;
		printerConfigService: any | null;
		configurationUnsubscribe: (() => void) | null;
		voidDialogVisible: boolean;
		labelToVoid: any | null;
		showReturnLabelWarning: boolean;
		voidDialogReturnLabelCount: number;
		voidDialogOutboundLabelId: string | null;
		voidingLabels: { [key: number]: boolean };
		shippingLabelCache: { [key: number]: any };
		allLabelsCache: { [key: number]: any[] }; // All labels for multi-package shipments
		creatingReturnLabels: { [key: number]: boolean };
		returnLabelCache: { [key: number]: any };
		returnLabelsByOutboundId: { [key: string]: any };
		allReturnLabelsCache: { [key: number]: any[] }; // All non-voided return labels (one per package on multi-package shipments)
		actionMenuOpen: { [key: number]: boolean };
		unpickDialog: boolean;
		unpickTarget: PickTicket | null;
		unpicking: boolean;
		unpickError: string;
	} {
		return {
			ticketsPick: [],
			notShipped: true,
			notShipped2: true,
			showDateByRow: null,
			showPrintedDateByRow: null,
			showItemTableByRow: null,
			showSubTableByRow: null,
			showTable: [""],
			ptUpdated: false,
			tempTrackingNumbers: {},
			editTrackingMode: {},
			labelUrls: {},
			labelPdfRefs: {},
			hardwareService: null,
			hardwareAvailable: false,
			availablePrinters: [],
			selectedPrinterId: null,
			printingLabels: {},
			printingReturnLabels: {},
			printerConnecting: false,
			printerConfigService: null,
			configurationUnsubscribe: null,
			voidDialogVisible: false,
			labelToVoid: null,
			showReturnLabelWarning: false,
			voidDialogReturnLabelCount: 0,
			voidDialogOutboundLabelId: null as string | null,
			voidingLabels: {},
			shippingLabelCache: {},
			allLabelsCache: {},
			creatingReturnLabels: {},
			returnLabelCache: {},
			returnLabelsByOutboundId: {},
			allReturnLabelsCache: {},
			actionMenuOpen: {} as { [key: number]: boolean },
			unpickDialog: false,
			unpickTarget: null,
			unpicking: false,
			unpickError: "",
		};
	},
	computed: {
		isUsingSharedConfig() {
			// Check if the selected printer matches the shared configuration
			if (!this.printerConfigService) return false;

			const config = this.printerConfigService.getConfiguration();
			const configPrinterId = config.pickTicketPrinterId || config.labelPrinterId;

			return this.selectedPrinterId === configPrinterId;
		},
	},
	methods: {
		async fetchShippingLabel(pickTicketId: number) {
			try {
				const result = await this.$apollo.query({
					query: GET_SHIPPING_LABEL_BY_PICK_TICKET,
					variables: { pickTicketId },
					fetchPolicy: "no-cache",
				});

				if (result.data?.shippingLabels && result.data.shippingLabels.length > 0) {
					const allLabels = result.data.shippingLabels;
					const primaryLabel = allLabels[0]; // First label (packageSequence = 1)

					// Cache all labels for multi-package support
					this.$set(this.allLabelsCache, pickTicketId, allLabels);

					// Cache the primary label for backward compatibility
					this.$set(this.shippingLabelCache, pickTicketId, primaryLabel);

					// Store the tracking URL from the shipping label if it exists
					if (primaryLabel.trackingUrl) {
						this.$set(this.trackingUrls, pickTicketId, primaryLabel.trackingUrl);
					}

					// For multi-package shipments, use combinedLabelData (contains all labels in one PDF)
					// Fall back to individual labelData if combined isn't available
					const labelUrlToUse = primaryLabel.combinedLabelData || primaryLabel.labelData;
					if (labelUrlToUse) {
						this.$set(this.labelUrls, pickTicketId, labelUrlToUse);
					}

					// Stored-label reference: View uses the combined PDF when one exists; printing never does.
					this.$set(this.labelPdfRefs, pickTicketId, {
						shippingLabelId: primaryLabel.shippingLabelId,
						combined: !!primaryLabel.combinedLabelData,
					});

					// Log multi-package info for debugging
					if (allLabels.length > 1) {
						console.log(`Multi-package shipment: ${allLabels.length} labels for pick ticket ${pickTicketId}`);
						console.log(`Using ${primaryLabel.combinedLabelData ? 'combined' : 'individual'} label URL`);
					}

					return primaryLabel;
				}
				return null;
			} catch (error) {
				console.error("Error fetching shipping label:", error);
				return null;
			}
		},

		async updatePickTicket(data: PickTicket) {
			const res = await this.$http.post(`/api/pick-tickets/${data.pickTicketId}`, {
				shippingDate: data.shippingDate,
				lastPrinted: data.printDate,
				trackingNumber: data.trackingNumber,
			});

			if (res.status == 200) {
				this.$bus.$emit("print-pickTicket");
				this.ptUpdated = true;

				setTimeout(() => {
					this.ptUpdated = false;
				}, 3000);
			} else {
				console.log(res);
			}
		},
		async hidePopupShipping(data: PickTicket, restoreInventory: boolean) {
			data.show = false;
			const pickTicketId = data.normalPickTicketId ?? data.pickTicketId;
			const res = await this.$http.post(`/api/pick-tickets/${pickTicketId}/unship`, {
				restoreInventory: restoreInventory,
			});

			if (res.status == 200) {
				setTimeout(() => {
					this.$bus.$emit("print-pickTicket");
				}, 1500);
			}

			console.log(res);
		},
		openUnpickDialog(data: PickTicket) {
			this.unpickTarget = data;
			this.unpickError = "";
			this.unpickDialog = true;
		},
		async confirmUnpick() {
			if (!this.unpickTarget) return;
			this.unpicking = true;
			this.unpickError = "";
			try {
				const res = await this.$http.post(
					`/api/pick-tickets/${this.unpickTarget.pickTicketId}/unpick`,
					{}
				);
				if (res.status === 200) {
					this.unpickDialog = false;
					// Same delayed refresh as unship — give the backend broadcast a beat.
					setTimeout(() => {
						this.$bus.$emit("print-pickTicket");
					}, 1500);
				}
			} catch (e: any) {
				this.unpickError =
					e?.response?.data?.message ?? "Failed to unpick the pick ticket.";
			} finally {
				this.unpicking = false;
			}
		},
		async hidePopupShippingItem(item: customLineItem, restoreInventory: boolean) {
			item.show = false;
			const pickTicketId = item.normalPickTicketId ?? item.pickTicketId;
			const res = await this.$http.post(
				`/api/pick-tickets/${pickTicketId}/lineItems/${item.lineItemId}/unship`,
				{
					restoreInventory: restoreInventory,
				}
			);
			if (res.status == 200) {
				setTimeout(() => {
					this.$bus.$emit("print-pickTicket");
				}, 1500);
			} else {
				console.log(res);
			}
		},
		handleSelectChange(e: any, data: any) {
			if (e === "NotShipped") {
				data.show = true;
			}
		},
		renderShippingStatus(item: { isShipped: boolean; shippingStatus: string }) {
			let shipStatus = {
				label: "",
				color: "",
			};
			if (item.shippingStatus === 'Processing') {
				shipStatus.label = "Processing";
				shipStatus.color = "table-status-orange";
			} else if (item.isShipped) {
				shipStatus.label = "Shipped";
				shipStatus.color = "table-status-green";
			} else {
				shipStatus.label = "Not Shipped";
				shipStatus.color = "table-status-gray";
			}
			return shipStatus;
		},

		async printPickTicket(id: number) {
			// Print pick ticket directly without checking for tracking number
			const res = await this.$http.post(`/api/orders/${this.orderId}/printPickTicket/${id}`);
			if (res.status == 200) {
				this.$bus.$emit("print-pickTicket");
				// this.showPrintPreview = true;
				this.$emit("printPickTicket", id);
			} else {
				console.log(res);
			}
		},

		async printAllPickTickets() {
			console.log(this.ticketsPick);

			// Print all pick tickets directly without checking for tracking numbers
			let promises = this.ticketsPick.map(async (pt) => {
				if (pt && pt.pickTicketId) {
					try {
						const res = await this.$http.post(
							`/api/orders/${this.orderId}/printPickTicket/${pt.pickTicketId}`
						);
						if (res.status !== 200) {
							console.log(res);
						}
					} catch (error) {
						console.log(error);
					}
				}
			});

			// Wait for all promises to resolve
			await Promise.all(promises);
			this.$emit(
				"printPickTicket",
				this.ticketsPick.map((pt) => pt.pickTicketId)
			);
		},

		parseDate(date: string | null) {
			//use with vuetify calander inputs
			if (!date) return null;
			const [month, day, year] = date.split("/");
			return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
		},

		hasDropShipItems(pickTicket: PickTicket) {
			return pickTicket.items.some((item) => item.dropShip);
		},
		isAllNonStockPickTicket(pickTicket: PickTicket) {
			return pickTicket.items.every((item) => item.stockedItem === false);
		},
		isAllStockPickTicket(pickTicket: PickTicket) {
			return pickTicket.items.every((item) => item.stockedItem === true);
		},
		hasNonStockItems(pickTicket: PickTicket) {
			return pickTicket.items.some((item) => item.stockedItem === false);
		},

		getTrackingUrl(trackingNumber: string, trackingUrl?: string) {
			// Use the saved tracking URL from ShipStation if available
			if (trackingUrl) {
				return trackingUrl;
			}

			// Otherwise, detect carrier and build URL
			return this.buildCarrierTrackingUrl(trackingNumber);
		},

		buildCarrierTrackingUrl(trackingNumber: string) {
			// Detect carrier based on tracking number format
			const carrier = this.detectCarrier(trackingNumber);

			switch (carrier) {
				case "ups":
					// UPS tracking URL format (2024)
					return `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber.replace(/,/g, "")}`;
				case "fedex":
					// FedEx tracking URL format (2024)
					return `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${trackingNumber}`;
				case "usps":
					// USPS tracking URL format (2024)
					return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`;
				default:
					// Default to UPS as fallback
					return `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber.replace(/,/g, "")}`;
			}
		},

		detectCarrier(trackingNumber: string) {
			// Remove spaces and convert to uppercase for consistent checking
			const cleaned = trackingNumber.replace(/\s/g, "").toUpperCase();

			// UPS tracking number patterns
			// 1Z followed by 16 characters
			if (/^1Z[A-Z0-9]{16}$/.test(cleaned)) {
				return "ups";
			}
			// UPS Mail Innovations: starts with MI
			if (/^MI\d{6,}/.test(cleaned)) {
				return "ups";
			}
			// UPS newer format: starts with T followed by 10 digits
			if (/^T\d{10}$/.test(cleaned)) {
				return "ups";
			}

			// FedEx tracking number patterns
			// 12 digits
			if (/^\d{12}$/.test(cleaned)) {
				return "fedex";
			}
			// 15 digits
			if (/^\d{15}$/.test(cleaned)) {
				return "fedex";
			}
			// FedEx Ground: 20 or 22 digits starting with specific prefixes
			if (/^(00)?\d{20}$/.test(cleaned)) {
				return "fedex";
			}

			// USPS tracking number patterns
			// USPS Priority Mail: 20 digits starting with 94
			if (/^94\d{18}$/.test(cleaned)) {
				return "usps";
			}
			// USPS Express Mail: starts with 9 and has 22 digits
			if (/^9\d{21}$/.test(cleaned)) {
				return "usps";
			}
			// USPS International: starts with two letters, 9 digits, ends with US
			if (/^[A-Z]{2}\d{9}US$/.test(cleaned)) {
				return "usps";
			}

			// Default to UPS if pattern doesn't match
			return "ups";
		},

		async saveManualTracking(data: PickTicket) {
			const trackingNumber = this.tempTrackingNumbers[data.pickTicketId];
			if (trackingNumber === data.trackingNumber) return;

			// Update the tracking number (allow empty string to clear)
			data.trackingNumber = trackingNumber || null;
			await this.updatePickTicket(data);

			// After saving, try to fetch the shipping label to get the tracking URL
			if (data.trackingNumber) {
				await this.fetchShippingLabel(data.pickTicketId);
			}

			// Exit edit mode after saving
			this.$set(this.editTrackingMode, data.pickTicketId, false);
		},

		initTempTracking(data: PickTicket) {
			// Initialize temp tracking with current value when field is focused
			if (
				!(data.pickTicketId in this.tempTrackingNumbers) ||
				!this.tempTrackingNumbers[data.pickTicketId]
			) {
				this.$set(this.tempTrackingNumbers, data.pickTicketId, data.trackingNumber || "");
			}
		},

		enableEditMode(data: PickTicket) {
			this.$set(this.editTrackingMode, data.pickTicketId, true);
			this.$set(this.tempTrackingNumbers, data.pickTicketId, data.trackingNumber || "");
		},

		cancelEditMode(data: PickTicket) {
			this.$set(this.editTrackingMode, data.pickTicketId, false);
			this.$set(this.tempTrackingNumbers, data.pickTicketId, data.trackingNumber || "");
		},

		// Per-entry tracking list for the pick ticket's "Tracking Number" field.
		// Each entry carries its own URL so outbound, drop-ship, and return
		// numbers all open their own tracking page (issue #1387 — reps need to
		// see return tracking numbers). Deduped by trimmed number, first wins.
		getTrackingDisplayEntries(data: PickTicket): { trackingNumber: string; url: string; isReturn: boolean }[] {
			const entries: { trackingNumber: string; url: string; isReturn: boolean }[] = [];
			const seen = new Set<string>();
			const push = (tn: string | null | undefined, url: string | undefined, isReturn: boolean) => {
				if (!tn) return;
				// Legacy PickTicket.TrackingNumber holds a comma-joined string for
				// multi-package shipments — split it so each number is its own entry
				// (and so dedup catches numbers already added from the labels cache).
				const parts = tn
					.split(",")
					.map((p) => p.trim())
					.filter(Boolean);
				for (const part of parts) {
					if (seen.has(part)) continue;
					seen.add(part);
					// A supplied URL only describes a single number; for split multi-package
					// strings fall back to carrier detection per number.
					const entryUrl = parts.length === 1 ? this.getTrackingUrl(part, url) : this.buildCarrierTrackingUrl(part);
					entries.push({ trackingNumber: part, url: entryUrl, isReturn });
				}
			};

			// Outbound labels (multi-package aware), each with its own ShipStation URL
			const outboundLabels = (this.allLabelsCache[data.pickTicketId] || []).filter((l: any) => !l.isVoided);
			for (const label of outboundLabels) {
				push(label.trackingNumber, label.trackingUrl, false);
			}

			// Legacy/manually-entered tracking on the pick ticket itself (also covers
			// the voided-label case where the number should still display)
			push(data.trackingNumber, this.trackingUrls[data.pickTicketId], false);

			// Drop-ship tracking from line items (carrier URL built from the number)
			if (data.items) {
				for (const item of data.items) {
					if (item.dropShip && item.trackingNumbers) {
						for (const tn of item.trackingNumbers) push(tn, undefined, false);
					}
				}
			}

			// Return labels last, tagged so the template renders "(Return)"
			for (const rl of this.allReturnLabelsCache[data.pickTicketId] || []) {
				push(rl.trackingNumber, rl.trackingUrl, true);
			}

			return entries;
		},

		openTrackingEntry(entry: { url: string }) {
			if (!entry.url) return;
			window.open(entry.url, "_blank");
		},

		// Consolidated carrier page for every number on the ticket — UPS et al.
		// accept multiple space-separated tracking numbers in one URL.
		openAllTrackingEntries(data: PickTicket) {
			const numbers = this.getTrackingDisplayEntries(data).map((e: { trackingNumber: string }) => e.trackingNumber);
			if (!numbers.length) return;
			window.open(this.buildCarrierTrackingUrl(numbers.join(" ")), "_blank");
		},

		openLabelUrl(labelUrl: string, pickTicketId?: number) {
			const ref = pickTicketId !== undefined ? this.labelPdfRefs[pickTicketId] : undefined;
			const primary = pickTicketId !== undefined ? this.shippingLabelCache[pickTicketId] : undefined;
			if (primary?.isVoided) return;
			if (ref) {
				window.open(labelPdfUrl(ref.shippingLabelId, ref.combined), "_blank");
				return;
			}
			window.open(labelUrl, "_blank");
		},

		getShipStationPortalUrl(pickTicket: PickTicket): string | null {
			if (!pickTicket.finalShipStationSalesOrderId || !pickTicket.finalShipStationShipmentId) {
				return null;
			}

			// Strip "se-" prefix from shipment ID if present
			const shipmentId = pickTicket.finalShipStationShipmentId.replace("se-", "");

			return `https://ship14.shipstation.com/orders/shipped/order/${pickTicket.finalShipStationSalesOrderId}/active/${shipmentId}`;
		},

		openShipStationPortal(portalUrl: string) {
			window.open(portalUrl, "_blank");
		},

		async initializeHardware() {
			try {
				console.log(
					"[PickTicketStatus] Initializing hardware integration with shared configuration"
				);

				// Initialize printer configuration service
				this.printerConfigService = getPrinterConfiguration();

				// Initialize hardware service - it will check settings from backend
				this.hardwareService = getHardwareService(true);

				// Initialize the service
				await this.hardwareService.initialize();

				// Check if we're connected
				const availability = await this.hardwareService.checkAvailability();
				console.log("[PickTicketStatus] Hardware availability:", availability);
				this.hardwareAvailable = availability.isAvailable;

				if (this.hardwareAvailable) {
					// Get available printers
					await this.discoverPrinters();

					// Load printer selection from shared configuration
					const config = this.printerConfigService.getConfiguration();
					const configPrinterId = config.pickTicketPrinterId || config.labelPrinterId;

					if (configPrinterId && this.availablePrinters.some((p) => p.id === configPrinterId)) {
						this.selectedPrinterId = configPrinterId;
						console.log("[PickTicketStatus] Using printer from shared config:", configPrinterId);
					} else {
						// Fallback to legacy storage if no shared config
						const savedPrinterId = localStorage.getItem("pick_ticket_printer_id");
						if (savedPrinterId && this.availablePrinters.some((p) => p.id === savedPrinterId)) {
							this.selectedPrinterId = savedPrinterId;
							// Update shared configuration with this printer
							this.printerConfigService.setPickTicketPrinter(savedPrinterId);
						}
					}

					console.log("[PickTicketStatus] Found printers:", this.availablePrinters.length);
				}

				// Subscribe to configuration changes
				this.configurationUnsubscribe = this.printerConfigService.onConfigurationChange(
					(config: PrinterConfiguration) => {
						console.log("[PickTicketStatus] Printer configuration changed:", config);
						const newPrinterId = config.pickTicketPrinterId || config.labelPrinterId;

						// Update selected printer if it's available
						if (newPrinterId && this.availablePrinters.some((p) => p.id === newPrinterId)) {
							this.selectedPrinterId = newPrinterId;
							console.log(
								"[PickTicketStatus] Updated printer selection from config:",
								newPrinterId
							);
						}
					}
				);
			} catch (error) {
				console.error("[PickTicketStatus] Error initializing hardware:", error);
				this.hardwareAvailable = false;
			}
		},

		async discoverPrinters() {
			try {
				if (!this.hardwareService) return;

				const printers = await this.hardwareService.discoverPrinters();
				this.availablePrinters = printers;
				console.log("[PickTicketStatus] Discovered printers:", this.availablePrinters);

				// Prefer Windows system printers over USB ones
				// If we have both USB and Windows version of the same printer, use Windows
				const windowsPrinters = printers.filter((p) => p.id.startsWith("windows-printer-"));
				if (windowsPrinters.length > 0 && !this.selectedPrinterId) {
					console.log("[PickTicketStatus] Found Windows printers, preferring those over USB");
				}
			} catch (error) {
				console.error("[PickTicketStatus] Error discovering printers:", error);
			}
		},

		onPrinterSelectionChanged(printerId: string | null) {
			// Update shared configuration service
			if (this.printerConfigService) {
				this.printerConfigService.setPickTicketPrinter(printerId);
				console.log("[PickTicketStatus] Updated printer in shared configuration:", printerId);
			}

			// Also update legacy storage for backward compatibility (will be handled by service)
			if (printerId) {
				localStorage.setItem("pick_ticket_printer_id", printerId);
			} else {
				localStorage.removeItem("pick_ticket_printer_id");
			}
		},

		async printLabel(pickTicketId: number, labelUrl: string) {
			if (!this.selectedPrinterId || !this.hardwareService || !labelUrl) {
				console.error("[PickTicketStatus] Cannot print: missing printer or label URL");
				return;
			}

			// Set loading state
			this.$set(this.printingLabels, pickTicketId, true);

			try {
				// Check if this is a multi-package shipment
				const allLabels = this.allLabelsCache[pickTicketId] || [];
				const primaryLabel = this.shippingLabelCache[pickTicketId];
				const isMultiPackage = allLabels.length > 1 || (primaryLabel?.totalPackages && primaryLabel.totalPackages > 1);
				const totalPackages = primaryLabel?.totalPackages || allLabels.length || 1;

				console.log(
					`[PickTicketStatus] Printing label for pick ticket ${pickTicketId}:`,
					{
						isMultiPackage,
						totalPackages,
						labelCount: allLabels.length,
						hasCombinedLabelData: !!primaryLabel?.combinedLabelData,
					}
				);

				// For multi-package shipments, print each label individually
				// This ensures all labels are printed even if UHB doesn't handle multi-page PDFs correctly
				if (isMultiPackage && allLabels.length > 1) {
					console.log(`[PickTicketStatus] Multi-package shipment: printing ${allLabels.length} labels individually`);
					let printedCount = 0;

					for (let i = 0; i < allLabels.length; i++) {
						const label = allLabels[i];

						if (!label.shippingLabelId && !label.labelData) {
							console.warn(`[PickTicketStatus] No label for package ${i + 1}, skipping`);
							continue;
						}

						console.log(`[PickTicketStatus] Printing package ${label.packageSequence || i + 1}/${totalPackages} (shippingLabelId ${label.shippingLabelId})`);

						const result = await printStoredLabel(this.hardwareService, this.selectedPrinterId, label, false);

						if (result.success) {
							printedCount++;
							console.log(`[PickTicketStatus] Package ${label.packageSequence || i + 1} printed successfully`);
						} else {
							console.error(`[PickTicketStatus] Failed to print package ${label.packageSequence || i + 1}:`, result.error);
						}

						// Small delay between labels to prevent printer buffer overflow
						if (i < allLabels.length - 1) {
							await new Promise((resolve) => setTimeout(resolve, 500));
						}
					}

					if (printedCount === allLabels.length) {
						this.$toast?.success(`Printed ${printedCount} of ${totalPackages} labels`);
					} else if (printedCount > 0) {
						this.$toast?.warning(`Printed ${printedCount} of ${totalPackages} labels (some failed)`);
					} else {
						throw new Error("Failed to print any labels");
					}
				} else {
					// Single package or no cached labels - use the provided URL
					console.log(`[PickTicketStatus] Single package: printing from URL:`, labelUrl);

					// Today this branch prints labelUrls[pickTicketId], which is the combined PDF when one exists
					// (a cache holding one label of a multi-package ticket lands here too); keep that exactly.
					const result = await printStoredLabel(
						this.hardwareService,
						this.selectedPrinterId,
						primaryLabel ?? { shippingLabelId: this.labelPdfRefs[pickTicketId]?.shippingLabelId, labelData: labelUrl },
						!!primaryLabel?.combinedLabelData
					);

					if (result.success) {
						this.$toast?.success("Label printed successfully");
					} else {
						throw new Error(result.error || "Print failed");
					}
				}
			} catch (error) {
				console.error("[PickTicketStatus] Error printing label:", error);
				this.$toast?.error(`Failed to print label: ${error.message}`);
			} finally {
				// Clear loading state
				this.$set(this.printingLabels, pickTicketId, false);
			}
		},

		async printAllLabels() {
			if (!this.selectedPrinterId || !this.hardwareService) {
				console.error("[PickTicketStatus] Cannot print: missing printer");
				return;
			}

			const labelsWithUrls = this.ticketsPick.filter((pt) => this.labelUrls[pt.pickTicketId]);
			if (labelsWithUrls.length === 0) {
				this.$store.dispatch("globalState/showNotification", {
					message: "No labels available to print",
					type: "warning",
				});
				return;
			}

			for (const pickTicket of labelsWithUrls) {
				await this.printLabel(pickTicket.pickTicketId, this.labelUrls[pickTicket.pickTicketId]);
			}
		},

		async clearTracking(data: PickTicket) {
			// Clear the tracking number
			this.$set(this.tempTrackingNumbers, data.pickTicketId, "");
			data.trackingNumber = null;
			await this.updatePickTicket(data);

			// Exit edit mode after clearing
			this.$set(this.editTrackingMode, data.pickTicketId, false);
		},

		onCreateLabel(pickTicketId: number) {
			this.$emit("create-shipping-label", pickTicketId);
		},

		handlePrinterSettingsUpdate(settings: any) {
			console.log("[PickTicketStatus] Received printer settings update:", settings);

			// Check if we have the configuration data
			if (settings.configuration) {
				const newPrinterId =
					settings.configuration.pickTicketPrinterId || settings.configuration.labelPrinterId;

				// Update selected printer if it's available
				if (newPrinterId && this.availablePrinters.some((p) => p.id === newPrinterId)) {
					this.selectedPrinterId = newPrinterId;
					console.log("[PickTicketStatus] Updated printer from settings event:", newPrinterId);
				}
			} else if (settings.selectedPrinterId) {
				// Fallback to legacy format
				if (this.availablePrinters.some((p) => p.id === settings.selectedPrinterId)) {
					this.selectedPrinterId = settings.selectedPrinterId;
					console.log(
						"[PickTicketStatus] Updated printer from legacy settings event:",
						settings.selectedPrinterId
					);
				}
			}
		},

		openHardwareSettings() {
			console.log("[PickTicketStatus] Opening hardware settings");

			// Emit event to show hardware status with printer settings dialog
			this.$bus?.$emit("hardware-status-show", {
				openPrinterSettings: true,
			});

			// Also ensure the hardware widget is visible
			this.$root.$emit("hardware-widget-toggle", true);
		},

		hasPendingSubItems(item) {
			// Find the original line item with its children from the lineItems prop
			const fullLineItem = this.findLineItemById(item.lineItemId);

			if (!fullLineItem || !fullLineItem.children || fullLineItem.children.length === 0) {
				return false;
			}

			// Check if any children are not in the current pick ticket
			const pickTicket = this.ticketsPick.find((pt) =>
				pt.items.some((i) => i.lineItemId === item.lineItemId)
			);

			if (!pickTicket) return false;

			// Get all line item IDs in the current pick ticket
			const pickTicketItemIds = new Set(pickTicket.items.map((i) => i.lineItemId));

			// Check if any children are missing from the pick ticket or have pending status
			return fullLineItem.children.some(
				(child) =>
					!pickTicketItemIds.has(child.lineItemId) ||
					child.status === "NeedsToBeOrdered" ||
					child.status === "Pending" ||
					child.status === "OnOrder"
			);
		},

		getPendingSubItems(item) {
			// Find the original line item with its children from the lineItems prop
			const fullLineItem = this.findLineItemById(item.lineItemId);

			if (!fullLineItem || !fullLineItem.children || fullLineItem.children.length === 0) {
				return [];
			}

			const pickTicket = this.ticketsPick.find((pt) =>
				pt.items.some((i) => i.lineItemId === item.lineItemId)
			);

			if (!pickTicket) return [];

			// Get all line item IDs in the current pick ticket
			const pickTicketItemIds = new Set(pickTicket.items.map((i) => i.lineItemId));

			// Return children that are not in the pick ticket or have pending status
			return fullLineItem.children
				.filter(
					(child) =>
						!pickTicketItemIds.has(child.lineItemId) ||
						child.status === "NeedsToBeOrdered" ||
						child.status === "Pending" ||
						child.status === "OnOrder"
				)
				.map((child) => ({
					styleCode: child.styleCode,
					vendorPartNumber: child.vendorPartNumber,
					description: child.description,
					status: child.status || "Pending",
				}));
		},

		findLineItemById(lineItemId) {
			// Recursive function to find a line item by ID
			const findInItems = (items) => {
				for (const item of items) {
					if (item.lineItemId === lineItemId) {
						return item;
					}
					if (item.children && item.children.length > 0) {
						const found = findInItems(item.children);
						if (found) return found;
					}
				}
				return null;
			};

			return findInItems(this.lineItems);
		},

		hasIncompleteItems(pickTicket) {
			// Check if any items in this pick ticket have pending sub-items
			return pickTicket.items.some((item) => this.hasPendingSubItems(item));
		},

		getStatusColor(status) {
			switch (status) {
				case "NeedsToBeOrdered":
					return "error";
				case "Pending":
					return "warning";
				case "OnOrder":
					return "info";
				default:
					return "grey";
			}
		},

		formatStatus(status) {
			switch (status) {
				case "NeedsToBeOrdered":
					return "Needs to be Ordered";
				case "Pending":
					return "Pending";
				case "OnOrder":
					return "On Order";
				default:
					return status || "Pending";
			}
		},

		// Void label functionality
		async confirmVoidLabel(pickTicketId: number) {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label) {
				// Fetch label if not cached
				const fetchedLabel = await this.fetchShippingLabel(pickTicketId);
				if (!fetchedLabel) {
					console.error("[PickTicketStatus] No label found for pick ticket:", pickTicketId);
					return;
				}
			}

			// Check if there's an associated return label
			const returnLabel = this.returnLabelCache[pickTicketId];
			this.showReturnLabelWarning = returnLabel && !returnLabel.isVoided;

			// Count active return labels for accurate dialog messaging
			try {
				const result = await this.$apollo.query({
					query: GET_RETURN_LABEL_BY_PICK_TICKET,
					variables: { pickTicketId },
					fetchPolicy: "no-cache",
				});
				const activeReturns = (result.data?.returnLabels || []).filter((rl: any) => !rl.isVoided);
				this.voidDialogReturnLabelCount = activeReturns.length;
			} catch {
				this.voidDialogReturnLabelCount = this.showReturnLabelWarning ? 1 : 0;
			}

			this.voidDialogOutboundLabelId = null; // Not needed for outbound void

			this.labelToVoid = {
				...this.shippingLabelCache[pickTicketId],
				pickTicketId,
			};
			this.voidDialogVisible = true;
		},

		async voidLabel() {
			if (!this.labelToVoid) return;

			const pickTicketId = this.labelToVoid.pickTicketId;
			const isReturnLabel = this.labelToVoid.isReturnLabel;
			this.$set(this.voidingLabels, pickTicketId, true);

			try {
				const result = await this.$apollo.mutate({
					mutation: VOID_SHIPPING_LABEL,
					variables: {
						labelId: this.labelToVoid.labelId,
					},
				});

				if (result.data?.voidShippingLabel?.success) {
					// Success notification
					const voidResult = result.data.voidShippingLabel;
					let message;

					if (isReturnLabel) {
						message = "Return label voided successfully. Refund will be processed by ShipStation.";
					} else {
						message =
							voidResult.message ||
							(voidResult.returnLabelVoided
								? "Label and associated return label voided successfully. Refunds will be processed by ShipStation."
								: "Label voided successfully. Refund will be processed by ShipStation.");
					}

					this.$store.dispatch("globalState/showNotification", {
						message: message,
						type: "success",
					});

					// Refresh both caches from server — do NOT mutate cached objects in-place
					// as they may be shared references (returnLabelCache and returnLabelsByOutboundId
					// can point to the same object, corrupting the wrong package's state)
					await this.fetchShippingLabel(pickTicketId);
					await this.fetchReturnLabel(pickTicketId);

					// Close dialog
					this.voidDialogVisible = false;
					this.labelToVoid = null;
					this.showReturnLabelWarning = false;
					this.voidDialogReturnLabelCount = 0;
					this.voidDialogOutboundLabelId = null;

					// Refresh pick ticket
					this.$bus.$emit("print-pickTicket");
				} else {
					throw new Error(result.data?.voidShippingLabel?.message || "Failed to void label");
				}
			} catch (error) {
				console.error("[PickTicketStatus] Error voiding label:", error);
				this.$store.dispatch("globalState/showNotification", {
					message: `Failed to void label: ${error.message}`,
					type: "error",
				});
			} finally {
				this.$set(this.voidingLabels, pickTicketId, false);
			}
		},

		cancelVoidDialog() {
			this.voidDialogVisible = false;
			this.labelToVoid = null;
			this.showReturnLabelWarning = false;
			this.voidDialogReturnLabelCount = 0;
			this.voidDialogOutboundLabelId = null;
		},

		async voidAllLabels(outboundLabelId: string) {
			if (!outboundLabelId || !this.labelToVoid) return;

			const pickTicketId = this.labelToVoid.pickTicketId;
			this.$set(this.voidingLabels, pickTicketId, true);

			try {
				// Void the outbound label — this cascades to all sibling packages + return labels
				const result = await this.$apollo.mutate({
					mutation: VOID_SHIPPING_LABEL,
					variables: { labelId: outboundLabelId },
				});

				if (result.data?.voidShippingLabel?.success) {
					this.$store.dispatch("globalState/showNotification", {
						message: "All labels voided successfully. Refunds will be processed by ShipStation.",
						type: "success",
					});

					// Refresh both caches from server (no in-place mutations — see voidLabel comment)
					await this.fetchShippingLabel(pickTicketId);
					await this.fetchReturnLabel(pickTicketId);

					this.voidDialogVisible = false;
					this.labelToVoid = null;
					this.showReturnLabelWarning = false;
					this.voidDialogReturnLabelCount = 0;
					this.voidDialogOutboundLabelId = null;

					this.$bus.$emit("print-pickTicket");
				} else {
					throw new Error(result.data?.voidShippingLabel?.message || "Failed to void labels");
				}
			} catch (error) {
				console.error("[PickTicketStatus] Error voiding all labels:", error);
				this.$store.dispatch("globalState/showNotification", {
					message: `Failed to void labels: ${error.message}`,
					type: "error",
				});
			} finally {
				this.$set(this.voidingLabels, pickTicketId, false);
			}
		},

		canVoidLabel(pickTicketId: number): boolean {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label || label.isVoided || !label.labelId) return false;

			// Check if within void window
			const createdDate = new Date(label.createdAt);
			const now = new Date();
			const daysSinceCreated = Math.floor(
				(now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
			);

			// Determine void window based on carrier
			const voidWindow = this.getVoidWindow(label.carrierCode);
			return daysSinceCreated <= voidWindow;
		},

		isLabelVoided(pickTicketId: number): boolean {
			const label = this.shippingLabelCache[pickTicketId];
			return label?.isVoided || false;
		},

		getVoidWindow(carrierCode: string): number {
			if (!carrierCode) return 30;
			const carrier = carrierCode.toLowerCase();
			// USPS: 28 days, UPS/FedEx: 30 days
			if (carrier.includes("usps") || carrier.includes("stamps")) {
				return 28;
			}
			return 30; // Default to 30 for UPS/FedEx
		},

		daysUntilVoidExpires(pickTicketId: number): number {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label) return 0;

			const createdDate = new Date(label.createdAt);
			const now = new Date();
			const daysSinceCreated = Math.floor(
				(now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
			);
			const voidWindow = this.getVoidWindow(label.carrierCode);
			return Math.max(0, voidWindow - daysSinceCreated);
		},

		isVoidWindowClosing(pickTicketId: number): boolean {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label || label.isVoided) return false;

			const daysLeft = this.daysUntilVoidExpires(pickTicketId);
			return daysLeft <= 3 && daysLeft > 0;
		},

		// Return label functionality
		canCreateReturnLabel(pickTicketId: number): boolean {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label || label.isVoided || !label.labelId) return false;

			// Check if return label already exists
			if (this.returnLabelCache[pickTicketId]) return false;

			// UPS Ground Saver does not support return labels
			// Source: https://help.shipstation.com/hc/en-us/articles/360035966572-UPS-Ground-Saver-formerly-UPS-SurePost#UUID-275185f7-414b-ebc6-5c80-951e463f26a4_UUID-94006792-12a9-fd3c-fc23-85ceaa22509c
			const serviceCode = label.serviceCode?.toLowerCase();
			if (serviceCode === 'ups_ground_saver') return false;

			return true;
		},

		hasReturnLabel(pickTicketId: number): boolean {
			return !!this.returnLabelCache[pickTicketId];
		},

		isReturnLabelUnsupported(pickTicketId: number): boolean {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label) return false;

			const serviceCode = label.serviceCode?.toLowerCase();
			return serviceCode === 'ups_ground_saver';
		},

		getReturnLabelDisabledTooltip(pickTicketId: number): string {
			if (this.isReturnLabelUnsupported(pickTicketId)) {
				return 'Return labels are not supported for UPS Ground Saver service';
			}
			return '';
		},

		getCreateReturnLabelDisabledReason(pickTicketId: number): string {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label) {
				return 'No label has been created';
			}
			if (label.isVoided) {
				return 'Label has been voided';
			}
			if (this.hasReturnLabel(pickTicketId)) {
				return 'Return label already exists';
			}
			if (this.isReturnLabelUnsupported(pickTicketId)) {
				return 'Return labels are not supported for UPS Ground Saver service';
			}
			return 'Return label not available';
		},

		getVoidReturnLabelDisabledReason(pickTicketId: number): string {
			const returnLabel = this.returnLabelCache[pickTicketId];
			if (!returnLabel) {
				return 'No return label has been created';
			}
			if (returnLabel.isVoided) {
				return 'Return label has already been voided';
			}
			// Check void window
			const createdDate = new Date(returnLabel.createdAt);
			const now = new Date();
			const daysSinceCreated = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
			const voidWindow = this.getVoidWindow(returnLabel.carrierCode);
			if (daysSinceCreated > voidWindow) {
				return `Void window has expired (${voidWindow} days)`;
			}
			return 'Cannot void return label';
		},

		getVoidLabelDisabledReason(pickTicketId: number): string {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label) {
				return 'No label has been created';
			}
			if (label.isVoided) {
				return 'Label has already been voided';
			}
			if (!label.labelId) {
				return 'No label ID available';
			}
			// Check void window
			const createdDate = new Date(label.createdAt);
			const now = new Date();
			const daysSinceCreated = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
			const voidWindow = this.getVoidWindow(label.carrierCode);
			if (daysSinceCreated > voidWindow) {
				return `Void window has expired (${voidWindow} days)`;
			}
			return 'Cannot void label';
		},

		async createReturnLabel(pickTicketId: number) {
			const label = this.shippingLabelCache[pickTicketId];
			if (!label) {
				console.error("[PickTicketStatus] No label found for pick ticket:", pickTicketId);
				return;
			}

			this.$set(this.creatingReturnLabels, pickTicketId, true);

			try {
				const result = await this.$apollo.mutate({
					mutation: CREATE_RETURN_LABEL,
					variables: {
						labelId: label.labelId,
						returnLabelMessages: null, // Server auto-generates default messages (rep initials + order number)
					},
				});

				// Check for GraphQL errors returned alongside data
				if (result.errors && result.errors.length > 0) {
					const errorMsg = result.errors.map((e: any) => e.message).join('; ');
					throw new Error(errorMsg);
				}

				if (result.data?.createReturnLabel?.label) {
					const returnLabel = result.data.createReturnLabel.label;

					// Refresh both caches so the action menu reflects current state
					await this.fetchShippingLabel(pickTicketId);
					await this.fetchReturnLabel(pickTicketId);

					// Success notification
					this.$store.dispatch("globalState/showNotification", {
						message: `Return label created! Tracking: ${returnLabel.trackingNumber}`,
						type: "success",
					});

					// Print the RETURN label itself; printLabel(pickTicketId, ...) would reprint the outbound labels on multi-package tickets.
					if (this.selectedPrinterId && this.hardwareService && (returnLabel.shippingLabelId || returnLabel.labelData)) {
						await printStoredLabel(this.hardwareService, this.selectedPrinterId, returnLabel, false);
					}
				} else {
					throw new Error('No return label data received from server');
				}
			} catch (error) {
				console.error("[PickTicketStatus] Error creating return label:", error);
				this.$store.dispatch("globalState/showNotification", {
					message: `Failed to create return label: ${error.message}`,
					type: "error",
				});
			} finally {
				this.$set(this.creatingReturnLabels, pickTicketId, false);
			}
		},

		viewReturnLabel(pickTicketId: number) {
			const returnLabel = this.returnLabelCache[pickTicketId];
			if (!returnLabel || returnLabel.isVoided) return;
			if (returnLabel.shippingLabelId) {
				window.open(labelPdfUrl(returnLabel.shippingLabelId, false), "_blank");
			} else if (returnLabel.labelData) {
				window.open(returnLabel.labelData, "_blank");
			}
		},

		async printReturnLabel(pickTicketId: number) {
			const returnLabel = this.returnLabelCache[pickTicketId];
			if (!returnLabel || (!returnLabel.shippingLabelId && !returnLabel.labelData)) {
				console.error("[PickTicketStatus] No return label data for pick ticket:", pickTicketId);
				return;
			}

			if (!this.hardwareService || !this.selectedPrinterId) {
				console.error("[PickTicketStatus] No hardware service or printer selected");
				return;
			}

			this.$set(this.printingReturnLabels, pickTicketId, true);

			try {
				console.log(`[PickTicketStatus] Printing return label for PT${pickTicketId}`);
				await printStoredLabel(this.hardwareService, this.selectedPrinterId, returnLabel, false);
				console.log(`[PickTicketStatus] Return label print sent for PT${pickTicketId}`);
			} catch (error) {
				console.error("[PickTicketStatus] Failed to print return label:", error);
			} finally {
				this.$set(this.printingReturnLabels, pickTicketId, false);
			}
		},

		getPrintReturnLabelDisabledReason(pickTicketId: number): string {
			if (!this.selectedPrinterId) {
				return 'No printer selected';
			}
			if (!this.hasReturnLabel(pickTicketId)) {
				return 'No return label has been created';
			}
			if (this.isReturnLabelVoided(pickTicketId)) {
				return 'Return label has been voided';
			}
			return 'Cannot print return label';
		},

		isReturnLabelVoided(pickTicketId: number): boolean {
			const returnLabel = this.returnLabelCache[pickTicketId];
			return returnLabel?.isVoided || false;
		},

		canVoidReturnLabel(pickTicketId: number): boolean {
			const returnLabel = this.returnLabelCache[pickTicketId];
			if (!returnLabel || returnLabel.isVoided || !returnLabel.labelId) return false;

			// Check if within void window
			const createdDate = new Date(returnLabel.createdAt);
			const now = new Date();
			const daysSinceCreated = Math.floor(
				(now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
			);

			// Determine void window based on carrier
			const voidWindow = this.getVoidWindow(returnLabel.carrierCode);
			return daysSinceCreated <= voidWindow;
		},

		async confirmVoidReturnLabel(pickTicketId: number) {
			const returnLabel = this.returnLabelCache[pickTicketId];
			if (!returnLabel) {
				console.error("[PickTicketStatus] No return label found for pick ticket:", pickTicketId);
				return;
			}

			// Show confirmation dialog
			this.showReturnLabelWarning = false; // This is for voiding return label only
			this.voidDialogReturnLabelCount = 0; // Not applicable when voiding a return label

			// Set outbound label ID for "Void All" option
			const outboundLabel = this.shippingLabelCache[pickTicketId];
			this.voidDialogOutboundLabelId = (outboundLabel && !outboundLabel.isVoided) ? outboundLabel.labelId : null;

			this.labelToVoid = {
				...returnLabel,
				pickTicketId,
				isReturnLabel: true, // Flag to indicate this is a return label
			};
			this.voidDialogVisible = true;
		},

		async fetchReturnLabel(pickTicketId: number) {
			try {
				const result = await this.$apollo.query({
					query: GET_RETURN_LABEL_BY_PICK_TICKET,
					variables: { pickTicketId },
					fetchPolicy: "no-cache",
				});

				if (result.data?.returnLabels && result.data.returnLabels.length > 0) {
					const allReturnLabels = result.data.returnLabels;
					this.$set(
						this.allReturnLabelsCache,
						pickTicketId,
						allReturnLabels.filter((rl: any) => !rl.isVoided)
					);
					// Keep backward compat - first non-voided return label
					const firstActive = allReturnLabels.find((rl: any) => !rl.isVoided) || allReturnLabels[0];
					this.$set(this.returnLabelCache, pickTicketId, firstActive);

					// Clear stale entries for this pick ticket's outbound labels before rebuilding
					const outboundLabels = this.allLabelsCache[pickTicketId] || [];
					for (const ol of outboundLabels) {
						if (ol.labelId && this.returnLabelsByOutboundId[ol.labelId]) {
							this.$delete(this.returnLabelsByOutboundId, ol.labelId);
						}
					}

					// Rebuild per-outbound lookup (prefer active over voided)
					for (const rl of allReturnLabels) {
						if (rl.originalLabelId) {
							const existing = this.returnLabelsByOutboundId[rl.originalLabelId];
							if (!existing || !rl.isVoided) {
								this.$set(this.returnLabelsByOutboundId, rl.originalLabelId, rl);
							}
						}
					}
					return firstActive;
				}
				this.$set(this.allReturnLabelsCache, pickTicketId, []);
				return null;
			} catch (error) {
				console.error("Error fetching return label:", error);
				return null;
			}
		},

		closeActionMenu(pickTicketId: number) {
			this.$set(this.actionMenuOpen, pickTicketId, false);
		},

		isMultiPackage(pickTicketId: number): boolean {
			const labels = this.allLabelsCache[pickTicketId];
			if (!labels) return false;
			return labels.filter((l: any) => !l.isVoided).length > 1;
		},

		getActivePackageLabels(pickTicketId: number): any[] {
			const labels = this.allLabelsCache[pickTicketId];
			if (!labels) return [];
			return labels.filter((l: any) => !l.isVoided);
		},

		getPackageDisplayLabel(label: any): string {
			const seq = label.packageSequence || '?';
			const tracking = label.trackingNumber || '';
			const suffix = tracking.length > 6 ? '...' + tracking.slice(-6) : tracking;
			return `Pkg ${seq} (${suffix})`;
		},

		viewLabelForPackage(label: any) {
			if (label.isVoided) return;
			if (label.shippingLabelId) {
				window.open(labelPdfUrl(label.shippingLabelId, false), '_blank');
			} else if (label.labelData) {
				window.open(label.labelData, '_blank');
			}
		},

		async printLabelForPackage(label: any) {
			if (!this.hardwareService || !this.selectedPrinterId || (!label.shippingLabelId && !label.labelData)) return;
			try {
				await printStoredLabel(this.hardwareService, this.selectedPrinterId, label, false);
			} catch (error) {
				console.error('[PickTicketStatus] Failed to print package label:', error);
			}
		},

		async createReturnLabelForPackage(label: any, pickTicketId: number) {
			this.$set(this.creatingReturnLabels, pickTicketId, true);
			try {
				const result = await this.$apollo.mutate({
					mutation: CREATE_RETURN_LABEL,
					variables: {
						labelId: label.labelId,
						returnLabelMessages: null,
					},
				});

				// Check for GraphQL errors returned alongside data
				if (result.errors && result.errors.length > 0) {
					const errorMsg = result.errors.map((e: any) => e.message).join('; ');
					throw new Error(errorMsg);
				}

				if (result.data?.createReturnLabel?.label) {
					const returnLabel = result.data.createReturnLabel.label;
					// Refresh both caches so the action menu reflects current state
					await this.fetchShippingLabel(pickTicketId);
					await this.fetchReturnLabel(pickTicketId);
					this.$store.dispatch('globalState/showNotification', {
						message: `Return label created for Pkg ${label.packageSequence}! Tracking: ${returnLabel.trackingNumber}`,
						type: 'success',
					});
					if (this.selectedPrinterId && this.hardwareService && (returnLabel.shippingLabelId || returnLabel.labelData)) {
						await printStoredLabel(this.hardwareService, this.selectedPrinterId, returnLabel, false);
					}
				} else {
					throw new Error('No return label data received from server');
				}
			} catch (error: any) {
				console.error('[PickTicketStatus] Error creating return label for package:', error);
				this.$store.dispatch('globalState/showNotification', {
					message: `Failed to create return label: ${error.message}`,
					type: 'error',
				});
			} finally {
				this.$set(this.creatingReturnLabels, pickTicketId, false);
			}
		},

		getReturnLabelForOutbound(outboundLabelId: string): any | null {
			const rl = this.returnLabelsByOutboundId[outboundLabelId];
			if (!rl || rl.isVoided) return null;
			return rl;
		},

		viewReturnLabelForPackage(outboundLabel: any) {
			const returnLabel = this.getReturnLabelForOutbound(outboundLabel.labelId);
			if (!returnLabel) return;
			if (returnLabel.shippingLabelId) {
				window.open(labelPdfUrl(returnLabel.shippingLabelId, false), '_blank');
			} else if (returnLabel.labelData) {
				window.open(returnLabel.labelData, '_blank');
			}
		},

		async printReturnLabelForPackage(outboundLabel: any) {
			const returnLabel = this.getReturnLabelForOutbound(outboundLabel.labelId);
			if (!returnLabel || (!returnLabel.shippingLabelId && !returnLabel.labelData) || !this.hardwareService || !this.selectedPrinterId) return;
			try {
				await printStoredLabel(this.hardwareService, this.selectedPrinterId, returnLabel, false);
			} catch (error) {
				console.error('[PickTicketStatus] Failed to print return label for package:', error);
			}
		},

		confirmVoidLabelForPackage(label: any, pickTicketId: number) {
			this.showReturnLabelWarning = false;
			this.voidDialogReturnLabelCount = 0;
			const returnLabel = this.getReturnLabelForOutbound(label.labelId);
			if (returnLabel && !returnLabel.isVoided) {
				this.showReturnLabelWarning = true;
				this.voidDialogReturnLabelCount = 1;
			}
			this.voidDialogOutboundLabelId = null;
			this.labelToVoid = {
				...label,
				pickTicketId,
			};
			this.voidDialogVisible = true;
		},

		confirmVoidReturnLabelForPackage(outboundLabel: any, pickTicketId: number) {
			const returnLabel = this.getReturnLabelForOutbound(outboundLabel.labelId);
			if (!returnLabel) return;
			this.showReturnLabelWarning = false;
			this.voidDialogReturnLabelCount = 0;
			this.voidDialogOutboundLabelId = (!outboundLabel.isVoided) ? outboundLabel.labelId : null;
			this.labelToVoid = {
				...returnLabel,
				pickTicketId,
				isReturnLabel: true,
			};
			this.voidDialogVisible = true;
		},
	},
	mounted() {
		// Only initialize hardware for users who need it (not reps).
		// Reps can view pick ticket status but don't need UHB WebSocket, scales, or printers.
		const user = this.$store.getters.userInfo;
		if (user && shouldEnableHardware(user)) {
			this.initializeHardware();
		}

		// Listen for printer settings updates from Hardware Widget
		this.$bus?.$on("printer-settings-updated", this.handlePrinterSettingsUpdate);
	},
	beforeDestroy() {
		// Clean up event listeners
		this.$bus?.$off("printer-settings-updated", this.handlePrinterSettingsUpdate);

		// Clean up configuration subscription
		if (this.configurationUnsubscribe) {
			this.configurationUnsubscribe();
			this.configurationUnsubscribe = null;
		}
	},
	watch: {
		pickTickets: {
			handler: async function () {
				console.log("PickTickets updated:", this.pickTickets);
				this.ticketsPick = this.pickTickets
					.filter((pt) => pt?.normalPickTicketId === null || pt?.normalPickTicketId === undefined)
					.map((item: PickTicket) => {
						// Initialize temp tracking number with current value
						if (item.trackingNumber) {
							this.$set(this.tempTrackingNumbers, item.pickTicketId, item.trackingNumber);
						}

						return {
							...item,
							show: false,
							decommit: false,
							removeTracking: false,
							items: item.items.map((x) => ({
								...x,
								show: false,
								decommit: false,
								removeTracking: false,
							})),
						};
					});

				// Fetch shipping labels and return labels for each pick ticket
				for (const pickTicket of this.ticketsPick) {
					if (
						pickTicket.trackingNumber &&
						(!this.trackingUrls[pickTicket.pickTicketId] ||
							!this.labelUrls[pickTicket.pickTicketId])
					) {
						await this.fetchShippingLabel(pickTicket.pickTicketId);
						// Also fetch return label if it exists
						await this.fetchReturnLabel(pickTicket.pickTicketId);
					}
				}
			},
			deep: true,
			immediate: true,
		},
	},
};
